import type {
  InboxWorkflowItem,
  InboxWorkflowItemStep,
  InboxWorkflowRowOutcome,
  InboxWorkflowRowOutcomeMap,
  InboxWorkflowState,
  InboxWorkflowStep,
  InboxWorkflowStepDefinition,
} from "./types"

type WorkflowStepLike = Pick<InboxWorkflowStep, "step_key" | "requirements_resolved_by_step">

const stepRequirements = (step: WorkflowStepLike) =>
  step.requirements_resolved_by_step.filter((requirement) => requirement.trim())

const itemHasRequirement = (item: InboxWorkflowItem, requirement: string) =>
  item.action_case.requirements.includes(requirement)

const itemRequirementIsPending = (item: InboxWorkflowItem, requirement: string) =>
  item.unresolved_requirements.includes(requirement) ||
  (itemHasRequirement(item, requirement) &&
    !item.action_case.resolved_requirements.includes(requirement))

export const workflowItemStepFor = (item: InboxWorkflowItem, stepKey: string) =>
  item.steps.find((entry) => entry.step_key === stepKey) ?? null

export const workflowStepIsOpen = (step: Pick<InboxWorkflowItemStep, "status"> | null) =>
  Boolean(step && step.status !== "resolved" && step.status !== "skipped")

export const workflowItemHasStep = (item: InboxWorkflowItem, stepKey: string) =>
  Boolean(workflowItemStepFor(item, stepKey))

export const workflowItemHasRelevantStep = (item: InboxWorkflowItem, step: WorkflowStepLike) =>
  workflowItemHasStep(item, step.step_key) ||
  stepRequirements(step).some((requirement) => itemHasRequirement(item, requirement))

export const workflowItemNeedsStepFor = (item: InboxWorkflowItem, step: WorkflowStepLike) => {
  const relevantRequirements = stepRequirements(step).filter((requirement) =>
    itemHasRequirement(item, requirement),
  )

  if (relevantRequirements.length) {
    return relevantRequirements.some((requirement) => itemRequirementIsPending(item, requirement))
  }

  const projectedStep = workflowItemStepFor(item, step.step_key)

  return workflowStepIsOpen(projectedStep)
}

export const workflowItemStepResolvedFor = (item: InboxWorkflowItem, step: WorkflowStepLike) => {
  const projectedStep = workflowItemStepFor(item, step.step_key)

  if (projectedStep?.status === "resolved") return true

  if (projectedStep?.status === "skipped") return false

  const relevantRequirements = stepRequirements(step).filter((requirement) =>
    itemHasRequirement(item, requirement),
  )

  return (
    Boolean(relevantRequirements.length) &&
    relevantRequirements.every(
      (requirement) =>
        item.action_case.resolved_requirements.includes(requirement) &&
        !itemRequirementIsPending(item, requirement),
    )
  )
}

export const stepIndexFor = (workflow: InboxWorkflowState, step: WorkflowStepLike) =>
  workflow.flow.steps.findIndex((candidate) => candidate.step_key === step.step_key)

export const itemHasRowsForStep = (
  item: InboxWorkflowItem,
  step: WorkflowStepLike,
  options: { removed?: boolean } = {},
) => item.compatible && !options.removed && workflowItemHasRelevantStep(item, step)

export const itemHasOpenRowsForStep = (
  item: InboxWorkflowItem,
  step: WorkflowStepLike,
  options: { removed?: boolean } = {},
) => item.compatible && !options.removed && workflowItemNeedsStepFor(item, step)

export const workflowStepFromDefinition = (
  definition: InboxWorkflowStepDefinition,
  itemSteps: Map<string, InboxWorkflowItemStep>,
  workflow: InboxWorkflowState,
  isRemovedForStep: (actionCaseId: string, stepIndex: number) => boolean,
  stepIndex: number,
): InboxWorkflowStep => {
  const projectedStep = itemSteps.get(definition.step_key)
  const relevantItems = workflow.items.filter((item) =>
    itemHasRowsForStep(item, definition, {
      removed: isRemovedForStep(item.action_case.id, stepIndex),
    }),
  )

  return {
    ...definition,
    status: relevantItems.length
      ? relevantItems.some((item) => workflowItemNeedsStepFor(item, definition))
        ? "pending"
        : "resolved"
      : "skipped",
    output: projectedStep?.output ?? {},
  }
}

export const buildFlowSteps = (
  workflow: InboxWorkflowState,
  isRemovedForStep: (actionCaseId: string, stepIndex: number) => boolean = () => false,
): InboxWorkflowStep[] => {
  const itemSteps = new Map<string, InboxWorkflowItemStep>()
  for (const item of workflow.items) {
    for (const step of item.steps) {
      if (!itemSteps.has(step.step_key)) itemSteps.set(step.step_key, step)
    }
  }

  return workflow.flow.steps.map((step, index) =>
    workflowStepFromDefinition(step, itemSteps, workflow, isRemovedForStep, index),
  )
}

export const hasRowsForStep = (
  workflow: InboxWorkflowState,
  step: WorkflowStepLike,
  isRemovedForStep: (actionCaseId: string, stepIndex: number) => boolean = () => false,
) => {
  const index = stepIndexFor(workflow, step)

  return workflow.items.some((item) =>
    itemHasRowsForStep(item, step, {
      removed: index >= 0 && isRemovedForStep(item.action_case.id, index),
    }),
  )
}

export const hasOpenRowsForStep = (
  workflow: InboxWorkflowState,
  step: WorkflowStepLike,
  isRemovedForStep: (actionCaseId: string, stepIndex: number) => boolean = () => false,
) => {
  const index = stepIndexFor(workflow, step)

  return workflow.items.some((item) =>
    itemHasOpenRowsForStep(item, step, {
      removed: index >= 0 && isRemovedForStep(item.action_case.id, index),
    }),
  )
}

export const nextOpenStepIndex = (
  workflow: InboxWorkflowState,
  afterIndex: number,
  isRemovedForStep: (actionCaseId: string, stepIndex: number) => boolean = () => false,
) => {
  const steps = workflow.flow.steps
  for (let index = afterIndex + 1; index < steps.length; index += 1) {
    if (hasOpenRowsForStep(workflow, steps[index], isRemovedForStep)) return index
  }

  return steps.length
}

export const firstRelevantStepIndex = (
  workflow: InboxWorkflowState,
  isRemovedForStep: (actionCaseId: string, stepIndex: number) => boolean = () => false,
) => {
  const index = workflow.flow.steps.findIndex((step) =>
    hasRowsForStep(workflow, step, isRemovedForStep),
  )

  return index >= 0 ? index : workflow.flow.steps.length
}

export const resolveActiveIndexAfterLoad = ({
  workflow,
  currentIndex,
  readOnly,
  isRemovedForStep,
}: {
  workflow: InboxWorkflowState
  currentIndex: number
  readOnly: boolean
  isRemovedForStep?: (actionCaseId: string, stepIndex: number) => boolean
}) => {
  const removed = isRemovedForStep ?? (() => false)
  const steps = workflow.flow.steps

  if (readOnly) {
    if (currentIndex < steps.length && hasRowsForStep(workflow, steps[currentIndex], removed)) {
      return currentIndex
    }

    return firstRelevantStepIndex(workflow, removed)
  }

  if (currentIndex >= steps.length) return nextOpenStepIndex(workflow, -1, removed)

  const activeStepDefinition = steps[currentIndex]

  if (activeStepDefinition && hasOpenRowsForStep(workflow, activeStepDefinition, removed)) {
    return currentIndex
  }

  return nextOpenStepIndex(workflow, -1, removed)
}

export const rowOutcomeMapForStep = (
  results: {
    step_key: string
    rows: {
      action_case_id: string
      status: string
      detail?: string | null
      error?: string | null
      output?: Record<string, unknown>
    }[]
  }[],
  stepKey: string,
): InboxWorkflowRowOutcomeMap => {
  const outcomes: InboxWorkflowRowOutcomeMap = {}
  for (const result of results) {
    if (result.step_key !== stepKey) continue

    for (const row of result.rows ?? []) {
      outcomes[row.action_case_id] = {
        status: row.status,
        detail: row.detail,
        error: row.error,
        output: row.output,
      } satisfies InboxWorkflowRowOutcome
    }
  }

  return outcomes
}

const statusFromRow = ({
  item,
  step,
  outcome,
}: {
  item: InboxWorkflowItem
  step: InboxWorkflowStep
  outcome?: InboxWorkflowRowOutcome | null
}): { state: string; label: string; detail?: string | null; hint?: string | null } => {
  if (outcome?.status === "failed") {
    return { state: "failed", label: "Fehler", detail: outcome.error ?? outcome.detail ?? null }
  }

  if (outcome?.status === "skipped") {
    return { state: "skipped", label: "Übersprungen", detail: outcome.detail ?? null }
  }

  if (outcome?.status === "success") {
    return { state: "resolved", label: "Erledigt", detail: outcome.detail ?? null }
  }

  if (!item.compatible)
    return { state: "disabled", label: "Nicht kompatibel", detail: item.error ?? null }

  if (workflowItemStepResolvedFor(item, step))
    return { state: "resolved", label: "Erledigt", detail: null }

  if (workflowItemNeedsStepFor(item, step)) return { state: "open", label: "Offen", detail: null }

  return { state: "skipped", label: "Nicht erforderlich", detail: null }
}

export const workflowTableRowStateFor = ({
  item,
  step,
  outcome,
}: {
  item: InboxWorkflowItem
  step: InboxWorkflowStep
  outcome?: InboxWorkflowRowOutcome | null
}) => {
  const projectedStep = workflowItemStepFor(item, step.step_key)
  const status = statusFromRow({ item, step, outcome })
  const outcomeResolved = outcome?.status === "success"
  const isResolved = outcomeResolved || workflowItemStepResolvedFor(item, step)
  const isOpen = !outcomeResolved && workflowItemNeedsStepFor(item, step)

  return {
    projectedStep,
    rowOutcome: outcome ?? null,
    isResolved,
    isOpen,
    isCompatible: item.compatible,
    disabled: outcome?.status === "success" || !isOpen || !item.compatible,
    statusLabel: status.label || "Offen",
    statusDetail: status.detail ?? null,
    statusHint: status.hint ?? null,
  }
}
